import { ActionTypes } from "../constants/action-types";
import { contentListToContentByDate } from "../ai-content/types/AIContentTypes";
import { fromJS, List, Map } from "immutable";
import { handleActions } from "redux-actions";
import { getDefaultTimezone } from "../ai-content/constants/timezoneConstants";
import { formatInTimeZone } from "date-fns-tz";
import { parseISO, isValid } from "date-fns";

const initialState = fromJS({
  generatedContent: "",
  error: null,
  settings: {},
  socialPosts: [],
  apiMetadata: null,
  platformFilter: "All", // Default to show all platforms
  statusFilter: "All",
  timezoneFilter: "All", // Default to show all timezones
  timezone: getDefaultTimezone(),
  currentDate: new Date(),

  /** When true, next generate must use override=true so new AI settings apply. Cleared when generate runs. */
  settingsChangedSinceLastGenerate: false,

  canvaDesigns: [],
  fileImages: [],
  showPostDetail: false,
  postDetail: null,

  showSharePost: false,
  sharePost: null,

  // TopicsModal state
  isTopicsModalOpen: false,
  selectedTopicIds: [],
  selectedTopic: null,
});

export const actions = {
  [ActionTypes.searchSocialPosts]: {
    next(state: Map<string, any>, action: any) {
      const { payload, error } = action;
      if (error || payload == null) {
        return state.merge({ error });
      }

      const data = payload?.data || payload;
      const pageResult = data?.pageResult || {};
      const posts: any[] = Array.isArray(pageResult?.content) ? pageResult.content : [];

      return state.merge({
        socialPosts: fromJS(posts),
        apiMetadata: data?.metadata || null,
        error: null,
      });
    },
  },

  /** Set when saveAIContentSetting succeeds so next generate uses override=true. */
  [ActionTypes.saveAIContentSetting]: {
    next(state: Map<string, any>, action: any) {
      const { payload, error } = action;
      if (error || !payload) return state;
      return state.merge({ settingsChangedSinceLastGenerate: true });
    },
  },

  [ActionTypes.clearAiSettingsChangedSinceLastGenerate]: {
    next(state: Map<string, any>) {
      return state.merge({ settingsChangedSinceLastGenerate: false });
    },
  },

  [ActionTypes.setCurrentDate]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const nextDate = payload?.currentDate;
      // Do not fall back to `new Date()` (today) — that jumps the fetch range
      // back to the current month and drops next-month posts.
      if (!(nextDate instanceof Date) || Number.isNaN(nextDate.getTime())) {
        return state;
      }
      return state.merge({
        currentDate: nextDate,
      });
    },
  },

  [ActionTypes.refreshCalendarData]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const dateRange = payload?.dateRange;
      const start = dateRange?.start;
      const end = dateRange?.end;
      if (!(start instanceof Date) || Number.isNaN(start.getTime())) return state;
      if (!(end instanceof Date) || Number.isNaN(end.getTime())) return state;
      // Keep as plain { start, end } so AIContentApp can read .start/.end after merge
      return state.merge({
        refreshTrigger: { start, end },
      });
    },
  },

  [ActionTypes.updateSocialPosts]: {
    next(state, { payload }) {
      if (payload === null || !payload.postDetail) {
        return state;
      }

      const postDetail = { ...payload.postDetail };
      if (postDetail.data) {
        postDetail.data = { ...postDetail.data };
        const scheduledTime = postDetail.data?.scheduledTime?.value;
        if (scheduledTime) {
          postDetail.data.scheduledTimeUTC = scheduledTime;
        }
      }

      const socialPosts = state.get("socialPosts").toJS();
      const nextPosts = socialPosts.map((item) =>
        item.id === postDetail?.id ? postDetail : item
      );
      // Use merge (not toJS/fromJS) so Date fields like currentDate stay intact
      return state.merge({
        socialPosts: fromJS(nextPosts),
      });
    },
  },

  [ActionTypes.updatePostDetail]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      // Use merge (not toJS/fromJS) so socialPosts and Date fields stay intact
      return state.merge({
        postDetail: fromJS(payload.postDetail),
      });
    },
  },

  [ActionTypes.setSocialPosts]: {
    next(state: Map<string, any>, { payload }) {
      const posts = Array.isArray(payload?.posts) ? payload.posts : [];
      if (posts.length === 0) {
        return state.merge({ socialPosts: fromJS([]) });
      }

      const toUtcIso = (raw: unknown): string | null => {
        if (raw == null) return null;

        const dateObj =
          typeof raw === "number"
            ? new Date(raw)
            : typeof raw === "string"
              ? (() => {
                  try {
                    return parseISO(raw);
                  } catch {
                    return null;
                  }
                })()
              : raw instanceof Date
                ? raw
                : null;

        return dateObj && isValid(dateObj)
          ? formatInTimeZone(dateObj, "UTC", "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
          : null;
      };

      const postsWithScheduledTimeUTC = posts.map((post) => {
        const raw = post?.data?.scheduledTime?.value ?? post?.data?.scheduledTime;
        const scheduledTimeUTC = raw ? toUtcIso(raw) : null;

        return scheduledTimeUTC ? { ...post, data: { ...post.data, scheduledTimeUTC } } : post;
      });

      return state.merge({ socialPosts: fromJS(postsWithScheduledTimeUTC) });
    },
  },

  [ActionTypes.showPostDetailDialog]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload == null || payload === undefined) {
        return state.merge({ pending, error });
      }
      // Use merge (not toJS/fromJS) so socialPosts and Date fields stay intact
      return state.merge({
        pending: false,
        showPostDetail: true,
        postDetail: fromJS(payload),
      });
    },
  },

  [ActionTypes.hidePostDetailDialog]: {
    next(state) {
      return state.merge({
        showPostDetail: false,
        postDetail: null,
      });
    },
  },

  [ActionTypes.updateSharePost]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        sharePost: payload.sharePost,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.showSharePostDialog]: {
    next(state, { payload }) {
      if (payload == null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        showSharePost: true,
        sharePost: payload.sharePost,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.hideSharePostDialog]: {
    next(state, {}) {
      const newState = {
        ...state.toJS(),
        showSharePost: false,
        sharePost: null,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.getCanvaDesigns]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({
          pending: false,
          canvaDesigns: fromJS([]),
        });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload == null || payload === undefined) {
        return state.merge({
          pending: false,
          canvaDesigns: fromJS([]),
        });
      }
      const designs = payload?.data?.items || payload?.items || [];
      return state.merge({
        pending: false,
        canvaDesigns: fromJS(designs),
      });
    },
  },

  [ActionTypes.getFileImages]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload == null || payload === undefined) {
        return state.merge({ pending, error });
      }
      return state.merge({
        pending: false,
        fileImages: fromJS(payload?.pageResult?.content || []),
      });
    },
  },

  // TopicsModal actions
  [ActionTypes.openTopicsModal]: {
    next(state: any, {}) {
      const newState = {
        ...state.toJS(),
        isTopicsModalOpen: true,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.closeTopicsModal]: {
    next(state: any, {}) {
      const newState = {
        ...state.toJS(),
        isTopicsModalOpen: false,
        selectedTopicIds: [],
        selectedTopic: null,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.selectTopic]: {
    next(state: Map<string, any>, { payload }: any) {
      const { topicId } = payload;
      const currentSelectedIds: List<string> = state.get("selectedTopicIds");

      if (!currentSelectedIds.includes(topicId)) {
        const newSelectedIds = currentSelectedIds.push(topicId);
        return state.merge({
          selectedTopicIds: newSelectedIds,
        });
      }

      return state;
    },
  },

  [ActionTypes.deselectTopic]: {
    next(state: Map<string, any>, { payload }: any) {
      const { topicId } = payload;
      const currentSelectedIds: List<string> = state.get("selectedTopicIds");
      const newSelectedIds = currentSelectedIds.filter((id: string) => id !== topicId);

      return state.merge({
        selectedTopicIds: newSelectedIds,
      });
    },
  },

  [ActionTypes.selectAllTopics]: {
    next(state: Map<string, any>, { payload }: any) {
      const { topicIds } = payload;

      return state.merge({
        selectedTopicIds: fromJS(topicIds) as List<string>,
      });
    },
  },

  [ActionTypes.deselectAllTopics]: {
    next(state: Map<string, any>, {}: any) {
      return state.merge({
        selectedTopicIds: List<string>(),
      });
    },
  },

  [ActionTypes.confirmTopicSelection]: {
    next(state: Map<string, any>, { payload }: any) {
      const { selectedTopic } = payload;

      return state.merge({
        selectedTopic,
        isTopicsModalOpen: false,
      });
    },
  },

  [ActionTypes.generateContents]: {
    next(state: Map<string, any>, action: any) {
      const { payload, error } = action;
      if (error) {
        return state.merge({
          error,
        });
      }
      const data = payload?.data;
      const contentList = data?.result?.contentList;
      const generatedContents = contentListToContentByDate(contentList) || {};
      const apiMetadata =
        data != null
          ? {
              token_usage: {
                prompt_tokens: data.promptTokens ?? 0,
                completion_tokens: data.completionTokens ?? 0,
                total_tokens: (data.promptTokens ?? 0) + (data.completionTokens ?? 0),
              },
            }
          : null;
      return state.merge({
        generatedContents,
        apiMetadata,
        error: null,
      });
    },
  },

  [ActionTypes.clearSocialPosts]: {
    next(state: Map<string, any>) {
      return state.merge({
        socialPosts: List(),
        error: null,
      });
    },
  },

  [ActionTypes.setPlatformFilter]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const platform = payload?.platform || payload || "All";
      return state.merge({
        platformFilter: String(platform),
      });
    },
  },

  [ActionTypes.setStatusFilter]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const status = payload?.status || payload || "All";
      return state.merge({
        statusFilter: String(status),
      });
    },
  },

  [ActionTypes.setTimezoneFilter]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const timezone = payload?.timezone || payload || "All";
      return state.merge({
        timezoneFilter: String(timezone),
      });
    },
  },

  [ActionTypes.setTimezone]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const timezone = payload?.timezone || payload;
      if (!timezone) return state;
      return state.merge({
        timezone: String(timezone),
      });
    },
  },

  [ActionTypes.resetAIContentState]: {
    next() {
      return initialState;
    },
  },
};

export const aiContentReducer = handleActions(actions, initialState);
